// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import type { AxiosError, AxiosResponse } from "axios"
import { apiClient, isSessionProbe } from "@/queries/client"
import { maintenanceStore } from "@/stores/maintenance"
import { captureApiError } from "@/observability/sentry"

// Only 5xx, network errors (no `response`), and ECONNABORTED count as backend-down signals;
// 4xx are client errors and are intentionally excluded.
// The session probe is exempt: it re-asks its question on its own, so a single
// page load against an unreachable backend would spend the whole failure budget
// before anything else was even tried, and the full-screen overlay would cover
// the reconnect surface that owns this case (SessionUnavailable).
// Exported for direct invocation in unit tests (no real HTTP needed).
export function simulateResponseError(error: AxiosError): Promise<never> {
  const status = error.response?.status
  const isServerError = status !== undefined && status >= 500
  const isNetworkError = status === undefined
  const isTimeout = error.code === "ECONNABORTED"
  const isBackendDownSignal = isServerError || isNetworkError || isTimeout

  if (isBackendDownSignal && !isSessionProbe(error.config?.url)) {
    maintenanceStore.incrementFailures()
  }

  return Promise.reject(error)
}

// INV: a single success resets the failure counter immediately, not after N successes.
// Exported for direct invocation in unit tests.
export function simulateResponseSuccess(response: AxiosResponse): AxiosResponse {
  maintenanceStore.resetFailures()
  return response
}

// M7 observability interceptor — reports every failed request to Sentry (PRD US §27) so
// silent network failures become visible. Separate from simulateResponseError (maintenance
// store only); both run on the same apiClient.
// Exported for direct invocation in unit tests (no real HTTP needed).
export function reportRequestError(error: AxiosError): Promise<never> {
  captureApiError(error, {
    url: error.config?.url,
    method: error.config?.method,
    status: error.response?.status,
  })
  return Promise.reject(error)
}

// MUST be imported before the first API call so interceptors are wired in time.
// Module-level side-effect; subsequent imports are no-ops (bundler module cache).
apiClient.interceptors.response.use(simulateResponseSuccess, simulateResponseError)
apiClient.interceptors.response.use(undefined, reportRequestError)
